import { Typography } from 'beek-design-system';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import styled from 'styled-components';

type PlansSectionProps = {
  dataPlans: Record<string, string>[];
  width: string;
  deviceSection?: boolean;
};

const defaultProps = {
  deviceSection: false,
};

type ButtonPlanProps = {
  planType: string;
  width: string;
  deviceSection: boolean;
};

const ButtonPlan = styled.a<ButtonPlanProps>`
  display: ${(props): string =>
    props.planType === 'monthly' ? 'block' : 'flex'};
  justify-content: space-between;
  padding: 1.781rem 1rem 1.281rem 1rem;
  width: 100%;
  height: 6.563rem;
  background: ${(props): string =>
    props.planType === 'monthly'
      ? 'linear-gradient(180deg, var(--cold-blue) 0%, var(--sky-blue) 50%, var(--beek-blue) 100%);'
      : 'linear-gradient(180deg, var(--gray-70) 0%, var(--gray-100) 49.24%, var(--black) 100%), var(--white)'};
  border-radius: 0.75rem;
  text-align: left;
  cursor: pointer;
  text-decoration: none;
  margin-bottom: ${(props): string =>
    props.planType === 'monthly' && props.deviceSection ? '3.5rem' : '0.75rem'};
  @media ${({ theme }): string => theme.devices.laptop} {
    width: ${(props): string => props.width};
    height: 5.75rem;
    padding: 1.5rem 1rem 1rem 1rem;
    margin-bottom: ${(props): string =>
      props.planType === 'monthly' ? '0rem' : '0.75rem'};
    &:hover {
      box-shadow: 0 0.25rem 0.938rem 0 rgba(0, 0, 0, 0.25);
    }
  }
`;

const TextYearly = styled(Typography)`
  position: absolute;
`;

const TagContainer = styled.div`
  display: flex;
`;

const TagBase = styled.div`
  background-color: var(--bright-yellow);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 8.036rem;
  height: 1.5rem;
  border-radius: 0 0.125rem 0.125rem 0;
  margin-top: -1rem;
  margin-right: -1.5rem;
  transform: rotate(0deg);
  @media ${({ theme }): string => theme.devices.laptop} {
    width: 8.438rem;
  }
`;

const TagTop = styled.div`
  background-color: var(--bright-yellow);
  height: 1.1rem;
  width: 1.1rem;
  border-radius: 0.1rem;
  transform: translateY(2.18rem) translateX(-0.75rem) rotate(46deg);
  margin-right: -1.3rem;
  margin-top: -3rem;
  @media ${({ theme }): string => theme.devices.laptop} {
    transform: translateY(2.18rem) translateX(-0.75rem) rotate(43deg);
  }
`;

const BestOffer = styled(Typography)`
  white-space: nowrap;
  margin: 0.125rem 0.768rem;
  @media ${({ theme }): string => theme.devices.laptop} {
    margin: 0.125rem 0.75rem 0.125rem 0.25rem;
  }
`;

const PlansSection = (props: PlansSectionProps): JSX.Element => {
  const { dataPlans, width, deviceSection } = props;
  const t = useTranslations('landing');

  /* eslint-disable react/jsx-no-useless-fragment */
  return (
    <>
      {dataPlans?.map((plan, i) => {
        if (i === 0) {
          const yearlyAmount = plan.amount;
          const yearlyUnit = String(t(`units.${plan.intervalUnit}`));
          const { savedMoney } = plan;
          return (
            <Link href={`/signup?planId=${plan.id}`} key={plan.id} passHref>
              <ButtonPlan
                planType="yearly"
                width={width}
                deviceSection={deviceSection || false}
                data-testid="yearlyPlan"
                data-cy="yearlyPlan"
              >
                <div>
                  <Typography
                    type="headline3"
                    colorVariant="white"
                    marginBottom="0.25rem"
                  >
                    {`${t('home.yearlyPlan')}`}
                  </Typography>
                  <TextYearly type="body2" colorVariant="white">
                    {`${t('home.yearlyPlanDescription', {
                      yearlyAmount,
                      yearlyUnit,
                      savedMoney,
                    })}`}
                  </TextYearly>
                </div>
                <TagContainer>
                  <TagTop />
                  <TagBase>
                    <BestOffer type="overline" colorVariant="gray100">
                      {`${t('home.bestOffer')}`}
                    </BestOffer>
                  </TagBase>
                </TagContainer>
              </ButtonPlan>
            </Link>
          );
        }
        const monthlyAmount = plan.amount;
        const monthlyUnit = String(t(`units.${plan.intervalUnit}`));
        return (
          <Link href={`/signup?planId=${plan.id}`} key={plan.id} passHref>
            <ButtonPlan
              planType="monthly"
              width={width}
              deviceSection={deviceSection || false}
              data-testid="monthlyPlan"
              data-cy="monthlyPlan"
            >
              <Typography
                type="headline3"
                colorVariant="gray100"
                marginBottom="0.25rem"
              >
                {`${t('home.monthlyPlan')}`}
              </Typography>
              <Typography type="body2" colorVariant="gray100">
                {`${t('home.monthlyPlanDescription', {
                  monthlyAmount,
                  monthlyUnit,
                })}`}
              </Typography>
            </ButtonPlan>
          </Link>
        );
      })}
    </>
  );
};

PlansSection.defaultProps = defaultProps;

export default PlansSection;
